import type { Event, Message, Content } from '@/types';

/**
 * Convert API events to chat messages for display
 */
export function eventsToMessages(events: Event[]): Message[] {
  const messages: Message[] = [];

  for (const event of events) {
    if (!event.content) continue;

    const message: Message = {
      id: event.id,
      content: extractTextFromContent(event.content),
      role: event.content.role === 'user' ? 'user' : 'assistant',
      timestamp: new Date(event.timestamp),
      artifacts: [], // TODO: Extract artifacts from event.actions?.artifactDelta
      groundingMetadata: event.groundingMetadata,
    };

    const cm = (event as unknown as { customMetadata?: Record<string, unknown> }).customMetadata;
    if (cm && (typeof cm === 'object')) {
      const rawType = cm['feedback'] as unknown;
      const type = rawType === 'like' || rawType === 'dislike' ? (rawType as 'like' | 'dislike') : null;
      const ts = cm['feedback_timestamp'] as unknown;
      const parsedTs = ts ? new Date(ts as string) : undefined;
      if (type || parsedTs) {
        message.feedback = {
          eventId: event.id,
          type,
          timestamp: parsedTs,
        };
      }
    }

    messages.push(message);
  }

  return messages;
}

/**
 * Extract readable text from content parts
 */
function extractTextFromContent(content: Content): string {
  if (!content.parts || content.parts.length === 0) {
    return '';
  }

  const textParts = content.parts
    .filter(part => part.text && !part.thought) // Exclude thought parts
    .map(part => part.text)
    .filter(Boolean);

  return textParts.join('\n\n');
}

/**
 * Create a user message for sending to API
 */
export function createUserMessage(text: string): Content {
  return {
    role: 'user',
    parts: [{ text }]
  };
}

/**
 * Generate a unique message ID
 */
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}